import { maxOne, sumOne } from 'rise-ui/utils/array.js';
import { getCanvasTextSize } from 'rise-ui/utils/canvas.js';
import { getBoundsOfRectangle } from 'rise-ui/utils/math.js';

/**
 * @param {Element} element
 * @param {string|Element} selector
 * @return {boolean} Whether element matches selector
 */
export function elementMatches(element, selector) {
  return element === selector || ( element && typeof element.matches === 'function'
    && typeof selector === 'string' ? element.matches(selector) : false );
}

/**
 * @param {Element} element
 * @param {string|Element|Array.<string|Element>} [is]
 * @param {string|Element|Array.<string|Element>} [isNot]
 * @return {boolean} Whether one of element/parents matches is/isNot
 */
export function elementFilter(element, is, isNot) {
  is = [].concat(is || []);
  const hasIs = !!is.length;
  let isIt = false;
  isNot = [].concat(isNot || []);
  let i;

  while (element) {
    for (i = 0; i < isNot.length; ++i) {
      if (elementMatches(element, isNot[i])) {
        return false;
      }
    }
    if (hasIs && !isIt) {
      for (i = 0; i < is.length; ++i) {
        if (elementMatches(element, is[i])) {
          isIt = true;
          break;
        }
      }
    }
    element = element.parentElement;
  }

  return !hasIs || isIt;
}

export function getSize(el) {
  const { width, height } = el.getBoundingClientRect();
  return { width, height };
}

export function isOverflowing(el) {
  return el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
}

export function getFitText(text, options) {
  text = text || '';
  options = options || {};
  const width = options.width || Infinity;

  const wordOptions = {
    ...options,
    rotate: undefined,
  };
  const spaceSize = getCanvasTextSize(' ', wordOptions);
  const result = { width: 0, height: 0, lines: [], sizes: [] };
  let line;
  let lineSize;
  const resetLine = () => {
    line = '';
    lineSize = { width: 0, height: 0 };
  };
  resetLine();
  const addLine = (text, size) => {
    result.width = maxOne(size.width, result.width);
    result.height = sumOne(size.height, result.height);
    result.lines.push(text);
    result.sizes.push(size);
    resetLine();
  };

  // TODO balance text `options.balanceText`
  text.split(/\r?\n/).forEach(wrappedLine => {
    const wrapAndTrim = wrappedLine.trim();
    // If line is blank or contains only whitespace
    if (wrappedLine && !wrapAndTrim) {
      addLine('', { ...spaceSize, width: 0 });
      return;
    }
    wrapAndTrim.split(/\s+/).forEach(word => {
      const wordSize = getCanvasTextSize(word, wordOptions);
      const delimiterWidth = line ? spaceSize.width : 0;
      const addedWidth = lineSize.width + delimiterWidth + wordSize.width;
      if (addedWidth <= width || !line) {
        // If the new text fits on the current line or there is no current line
        const delimiter = line ? ' ' : '';
        line += delimiter + word;
        lineSize = {
          width: sumOne((delimiter ? spaceSize.width : 0) + wordSize.width, lineSize.width),
          height: maxOne(spaceSize.height, maxOne(wordSize.height, lineSize.height)),
        };
      } else {
        // If there is a current line to end before adding the new word
        addLine(line, lineSize);
        line = word;
        lineSize = wordSize;
      }
    });
    if (line) {
      addLine(line, lineSize);
    }
  });

  if (options.rotate) {
    const rotatedSize = getBoundsOfRectangle(result, options.rotate);
    result.width = rotatedSize.width;
    result.height = rotatedSize.height;
  }

  return result;
}

export function resizeRect(rect, width, height, ratio = 1) {
  let resized = false;
  if (width !== null && width !== undefined) {
    if (rect instanceof HTMLCanvasElement) {
      rect.style.width = `${Math.floor(width)}px`;
      width *= ratio;
    }
    width = Math.floor(width);
  }
  if (height !== null && height !== undefined) {
    if (rect instanceof HTMLCanvasElement) {
      rect.style.height = `${Math.floor(height)}px`;
      height *= ratio;
    }
    height = Math.floor(height);
  }
  if (width !== rect.width) {
    rect.width = width;
    resized = true;
  }
  if (height !== rect.height) {
    rect.height = height;
    resized = true;
  }
  return resized;
}

export function resizeStyleRect(rect, width, height) {
  let resized = false;
  if (width !== null && width !== undefined) {
    width = Math.floor(width) + 'px';
  }
  if (height !== null && height !== undefined) {
    height = Math.floor(height) + 'px';
  }
  if (width !== rect.width) {
    rect.width = width;
    resized = true;
  }
  if (height !== rect.height) {
    rect.height = height;
    resized = true;
  }
  return resized;
}

export const undraggable = el => {
  if (el) {
    el.setAttribute?.('draggable', 'false');
    el.addEventListener('dragstart', e => {
      e.preventDefault();
      return false;
    });
  }
  return el;
};
